import React, { useState, useEffect } from 'react';
import {
  FiInfo,
  FiMapPin,
  FiUsers,
  FiSettings,
  FiCalendar,
  FiUser,
  FiMail,
  FiPhone,
  FiActivity,
  FiEdit3,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiFileText,
  FiShield,
  FiTrendingUp,
  FiTarget,
  FiPlus,
  FiX,
  FiEdit,
  FiTrash2,
  FiEye
} from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../libs/axios';
import { BiBarChart } from 'react-icons/bi';
import { FaRupeeSign } from 'react-icons/fa';
import EntityLogs from '../../../utils/logs.utils';

// Updated Types/Interfaces
interface Role {
  id: number;
  name: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: Role;
}

interface UserProject {
  id: number;
  user_id: number;
  project_id: number;
  created_at: string;
  updated_at: string;
  user: User;
}

interface Activity {
  id: number;
  activity_name: string;
  activity_description: string;
  status: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
  created_at: string;
  updated_at: string;
}

interface Client {
  id: number;
  client_full_name: string;
  client_short_name: string;
  address: string;
  gst_number: string;
  state_circle_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
}

interface StateCircle {
  id: number;
  state_name: string;
  circle_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
}

interface Site {
  id: number;
  site_name: string;
  location: string;
  status: string;
  // Add other site properties as needed
}

interface Project {
  id: number;
  activity_id: number;
  client_id: number;
  state_circle_id: number;
  client_project_id: number | null;
  project_name: string;
  project_short_name: string;
  project_description: string;
  budget: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
  activity: Activity;
  client: Client;
  state_circle: StateCircle;
  client_project: any | null;
  user_projects: UserProject[];
  sites: Site[];
}

interface ProjectStatistics {
  total_sites: number;
  sites_by_status: Record<string, number>;
  completion_percentage: number;
}

interface ProjectData {
  project: Project;
  statistics: ProjectStatistics;
}

interface TabProps {
  data: ProjectData;
}

interface SiteOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  site?: Site | null;
  onSave: (siteData: any) => void;
}

interface SiteFormData {
  site_name: string;
  location: string;
  status: string;
  assigned_engineer: string;
  po_number: string;
  po_amount: string;
}

interface TeamMember {
  role: string;
  member: User;
  icon: React.ComponentType<{ size?: number }>;
  color: string;
}

// Site Management Offcanvas Component
// const SiteOffcanvas: React.FC<SiteOffcanvasProps> = ({ isOpen, onClose, site = null, onSave }) => {
//   const [formData, setFormData] = useState<SiteFormData>({
//     site_name: '',
//     location: '',
//     status: 'PENDING',
//     assigned_engineer: '',
//     po_number: '',
//     po_amount: ''
//   });

//   useEffect(() => {
//     if (site) {
//       setFormData({
//         site_name: site.site_name,
//         location: site.location,
//         status: site.status,
//         assigned_engineer: site.assigned_engineer ? `${site.assigned_engineer.first_name} ${site.assigned_engineer.last_name}` : '',
//         po_number: site.purchase_order?.po_number || '',
//         po_amount: site.purchase_order?.amount?.toString() || ''
//       });
//     } else {
//       setFormData({
//         site_name: '',
//         location: '',
//         status: 'PENDING',
//         assigned_engineer: '',
//         po_number: '',
//         po_amount: ''
//       });
//     }
//   }, [site]);

//   const handleSubmit = (e: React.FormEvent) => {
//     e.preventDefault();
//     onSave(formData);
//     onClose();
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 z-50 overflow-hidden">
//       <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
//       <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
//         <div className="flex flex-col h-full">
//           {/* Header */}
//           <div className="flex items-center justify-between p-4 border-b border-gray-200">
//             <h2 className="text-lg font-semibold text-gray-900">
//               {site ? 'Edit Site' : 'Add New Site'}
//             </h2>
//             <button
//               onClick={onClose}
//               className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
//             >
//               <FiX size={20} />
//             </button>
//           </div>

//           {/* Content */}
//           <div className="flex-1 overflow-y-auto p-4">
//             <form onSubmit={handleSubmit} className="space-y-4">
//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Site Name *
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.site_name}
//                   onChange={(e) => setFormData({ ...formData, site_name: e.target.value })}
//                   className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
//                   placeholder="Enter site name"
//                   required
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Location *
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.location}
//                   onChange={(e) => setFormData({ ...formData, location: e.target.value })}
//                   className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
//                   placeholder="Enter location"
//                   required
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Status
//                 </label>
//                 <select
//                   value={formData.status}
//                   onChange={(e) => setFormData({ ...formData, status: e.target.value })}
//                   className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
//                 >
//                   <option value="PENDING">Pending</option>
//                   <option value="IN_PROGRESS">In Progress</option>
//                   <option value="COMPLETED">Completed</option>
//                 </select>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Assigned Engineer
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.assigned_engineer}
//                   onChange={(e) => setFormData({ ...formData, assigned_engineer: e.target.value })}
//                   className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
//                   placeholder="Enter engineer name"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   PO Number
//                 </label>
//                 <input
//                   type="text"
//                   value={formData.po_number}
//                   onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
//                   className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
//                   placeholder="Enter PO number"
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   PO Amount
//                 </label>
//                 <input
//                   type="number"
//                   value={formData.po_amount}
//                   onChange={(e) => setFormData({ ...formData, po_amount: e.target.value })}
//                   className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
//                   placeholder="Enter PO amount"
//                 />
//               </div>
//             </form>
//           </div>

//           {/* Footer */}
//           <div className="p-4 border-t border-gray-200">
//             <div className="flex gap-2">
//               <button
//                 onClick={handleSubmit}
//                 className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors text-sm font-medium"
//               >
//                 {site ? 'Update Site' : 'Add Site'}
//               </button>
//               <button
//                 onClick={onClose}
//                 className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// Project Overview Tab Component
const ProjectOverviewTab: React.FC<TabProps> = ({ data }) => {
  const { project } = data;

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ACTIVE': return 'text-green-600 bg-green-100';
      case 'INACTIVE': return 'text-red-600 bg-red-100';
      case 'SUSPENDED': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="space-y-4">
      {/* Basic Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Project Information</h3>
          {/* <button className="flex items-center gap-1 px-3 py-1 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors text-sm">
            <FiEdit3 size={14} />
            Edit
          </button> */}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Project Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{project.project_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Short Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{project.project_short_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Activity</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiActivity className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{project.activity.activity_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Budget</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FaRupeeSign className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{parseFloat(project.budget).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
              <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                <FiUser className="text-gray-400 mt-0.5" size={14} />
                <div className="text-sm text-gray-900">
                  <div>{project.client.client_full_name}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{project.client.client_short_name}</div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State & Circle</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiMapPin className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{project.state_circle.state_name} - {project.state_circle.circle_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <div className={`w-2 h-2 rounded-full ${project.status === 'ACTIVE' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Created Date</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{new Date(project.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {project.project_description && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-sm text-gray-900">{project.project_description}</p>
            </div>
          </div>
        )}
      </div>

      {/* Client Information */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Client Details</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">GST Number</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiFileText className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{project.client.gst_number}</span>
            </div>
          </div>

          {/* <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
              <FiMapPin className="text-gray-400" size={14} />
              <span className="text-sm text-gray-900">{project.client.state}</span>
            </div>
          </div> */}
        </div>

        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
          <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
            <FiMapPin className="text-gray-400 mt-0.5" size={14} />
            <span className="text-sm text-gray-900">{project.client.address}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Team Members Tab Component
const TeamMembersTab: React.FC<TabProps> = ({ data }) => {
  const { project } = data;

  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getColorForRole = (roleName: string): string => {
    const colorMap: Record<string, string> = {
      'Site Designer': 'bg-blue-100 text-blue-700 border-blue-200',
      'Quality Staff': 'bg-green-100 text-green-700 border-green-200',
      'Project Manager': 'bg-purple-100 text-purple-700 border-purple-200',
      'Team Coordinator': 'bg-orange-100 text-orange-700 border-orange-200',
      'Technical Associate': 'bg-pink-100 text-pink-700 border-pink-200',
      'MIS': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
    return colorMap[roleName] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getAvatarColorForRole = (roleName: string): string => {
    const colorMap: Record<string, string> = {
      'Site Designer': 'bg-blue-500',
      'Quality Staff': 'bg-green-500',
      'Project Manager': 'bg-purple-500',
      'Team Coordinator': 'bg-orange-500',
      'Technical Associate': 'bg-pink-500',
      'MIS': 'bg-indigo-500',
    };
    return colorMap[roleName] || 'bg-gray-500';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Project Team</h3>
            <p className="text-sm text-gray-500 mt-1">
              {project.user_projects.length} team {project.user_projects.length === 1 ? 'member' : 'members'}
            </p>
          </div>
        </div>

        {project.user_projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <FiUser size={24} className="text-gray-400" />
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">No team members assigned</h4>
            <p className="text-sm text-gray-500">Team members will appear here once assigned to this project.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {(() => {
              const grouped: Record<string, typeof project.user_projects> = {};
              project.user_projects.forEach((up) => {
                const roleName = up.user.role.name;
                if (!grouped[roleName]) grouped[roleName] = [];
                grouped[roleName].push(up);
              });
              const sortedRoles = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
              return sortedRoles.map((roleName) => {
                const members = [...grouped[roleName]].sort((a, b) =>
                  `${a.user.first_name} ${a.user.last_name}`.localeCompare(`${b.user.first_name} ${b.user.last_name}`)
                );
                const roleColorClass = getColorForRole(roleName);
                const avatarColorClass = getAvatarColorForRole(roleName);
                return (
                  <div key={roleName}>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full inline-block mb-3 border ${roleColorClass}`}>
                      {roleName} ({members.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {members.map((userProject) => {
                        const { user } = userProject;
                        const fullName = `${user.first_name} ${user.last_name}`;
                        const initials = getInitials(user.first_name, user.last_name);
                        return (
                          <div
                            key={userProject.id}
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-gray-300 transition-all duration-200"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-12 h-12 rounded-full ${avatarColorClass} flex items-center justify-center flex-shrink-0`}>
                                <span className="text-white font-semibold text-sm">{initials}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-gray-900 truncate mb-1">{fullName}</h4>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${roleColorClass} mb-2`}>
                                  {user.role.name}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Additional Info Section */}
        {project.user_projects.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <div className="flex gap-6">
                <div>
                  <span className="text-gray-500">Total Members:</span>
                  <span className="ml-2 font-medium text-gray-900">{project.user_projects.length}</span>
                </div>
                {/* <div>
                  <span className="text-gray-500">Roles:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {new Set(project.user_projects.map(up => up.user.role.name)).size}
                  </span>
                </div> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Sites Overview Tab Component
// const SitesOverviewTab: React.FC<TabProps> = ({ data }) => {
//   const { project, statistics } = data;
//   const [siteOffcanvasOpen, setSiteOffcanvasOpen] = useState(false);
//   const [editingSite, setEditingSite] = useState<Site | null>(null);
//   // Use mock sites instead of API sites for demonstration


//   const getStatusColor = (status: string): string => {
//     switch (status) {
//       case 'COMPLETED': return 'text-green-600 bg-green-100';
//       case 'IN_PROGRESS': return 'text-blue-600 bg-blue-100';
//       case 'PENDING': return 'text-yellow-600 bg-yellow-100';
//       default: return 'text-gray-600 bg-gray-100';
//     }
//   };

//   const getStatusIcon = (status: string) => {
//     switch (status) {
//       case 'COMPLETED': return <FiCheckCircle className="w-3 h-3" />;
//       case 'IN_PROGRESS': return <FiClock className="w-3 h-3" />;
//       case 'PENDING': return <FiAlertCircle className="w-3 h-3" />;
//       default: return <FiClock className="w-3 h-3" />;
//     }
//   };

//   const handleAddSite = (): void => {
//     setEditingSite(null);
//     setSiteOffcanvasOpen(true);
//   };

//   const handleEditSite = (site: Site): void => {
//     setEditingSite(site);
//     setSiteOffcanvasOpen(true);
//   };

//   const handleDeleteSite = (siteId: number): void => {
//     setSites(sites.filter(site => site.id !== siteId));
//   };

//   const handleSaveSite = (siteData: any): void => {
//     console.log('Site saved:', siteData);
//     // In a real app, you would make an API call here
//   };

//   return (
//     <div className="space-y-4">
//       {/* Statistics Cards */}
//       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//         <div className="bg-white p-4 rounded shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-xs font-medium text-gray-600">Total Sites</p>
//               <p className="text-xl font-bold text-gray-900 mt-1">{mockStatistics.total_sites}</p>
//             </div>
//             <div className="p-2 bg-blue-100 rounded">
//               <FiMapPin className="w-4 h-4 text-blue-600" />
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-4 rounded shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-xs font-medium text-gray-600">Completion</p>
//               <p className="text-xl font-bold text-green-600 mt-1">{mockStatistics.completion_percentage}%</p>
//             </div>
//             <div className="p-2 bg-green-100 rounded">
//               <FiTarget className="w-4 h-4 text-green-600" />
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-4 rounded shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-xs font-medium text-gray-600">In Progress</p>
//               <p className="text-xl font-bold text-blue-600 mt-1">{mockStatistics.sites_by_status.IN_PROGRESS || 0}</p>
//             </div>
//             <div className="p-2 bg-blue-100 rounded">
//               <FiClock className="w-4 h-4 text-blue-600" />
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-4 rounded shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-xs font-medium text-gray-600">Pending</p>
//               <p className="text-xl font-bold text-yellow-600 mt-1">{mockStatistics.sites_by_status.PENDING || 0}</p>
//             </div>
//             <div className="p-2 bg-yellow-100 rounded">
//               <FiAlertCircle className="w-4 h-4 text-yellow-600" />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Sites List */}
//       <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
//         {sites.length === 0 ? (
//           <div className="p-8 text-center">
//             <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
//               <FiMapPin className="w-6 h-6 text-gray-400" />
//             </div>
//             <h3 className="text-base font-medium text-gray-900 mb-1">No sites added yet</h3>
//             <p className="text-sm text-gray-600 mb-4">Sites will appear here once they are added to this project.</p>
//           </div>
//         ) : (
//           <div className="divide-y divide-gray-200">
//             {sites.map((site: Site) => (
//               <div key={site.id} className="p-4 hover:bg-gray-50 transition-colors duration-200">
//                 <div className="flex items-center justify-between">
//                   <div className="flex-1">
//                     <div className="flex items-center gap-2 mb-1">
//                       <h4 className="text-base font-semibold text-gray-900">{site.site_name}</h4>
//                       <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(site.status)}`}>
//                         {getStatusIcon(site.status)}
//                         {site.status.replace('_', ' ')}
//                       </span>
//                     </div>
//                     <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
//                       <FiMapPin className="w-3 h-3" />
//                       {site.location}
//                     </p>

//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
//                       <div className="flex items-center gap-1">
//                         <FiUser className="w-3 h-3 text-gray-400" />
//                         <span className="text-gray-600">Engineer:</span>
//                         <span className="font-medium">
//                           {site.assigned_engineer
//                             ? `${site.assigned_engineer.first_name} ${site.assigned_engineer.last_name}`
//                             : 'Not assigned'
//                           }
//                         </span>
//                       </div>

//                       <div className="flex items-center gap-1">
//                         <FiFileText className="w-3 h-3 text-gray-400" />
//                         <span className="text-gray-600">PO:</span>
//                         <span className="font-medium">
//                           {site.purchase_order?.po_number || 'Not assigned'}
//                         </span>
//                       </div>

//                       <div className="flex items-center gap-1">
//                         <FiCalendar className="w-3 h-3 text-gray-400" />
//                         <span className="text-gray-600">Created:</span>
//                         <span className="font-medium">
//                           {new Date(site.created_at).toLocaleDateString()}
//                         </span>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="flex items-center gap-1 ml-4">
//                     <button
//                       onClick={() => console.log('View site:', site.id)}
//                       className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
//                       title="View Site"
//                     >
//                       <FiEye size={14} />
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         )}
//       </div>

//       {/* Site Management Offcanvas */}
//       <SiteOffcanvas
//         isOpen={siteOffcanvasOpen}
//         onClose={() => setSiteOffcanvasOpen(false)}
//         site={editingSite}
//         onSave={handleSaveSite}
//       />
//     </div>
//   );
// };

// Analytics Tab Component
// const AnalyticsTab: React.FC<TabProps> = ({ data }) => {
//   // Use mock sites for analytics
//   const mockStatistics = {
//     total_sites: mockSites.length,
//     sites_by_status: mockSites.reduce((acc, site) => {
//       acc[site.status] = (acc[site.status] || 0) + 1;
//       return acc;
//     }, {} as Record<string, number>),
//     completion_percentage: Math.round((mockSites.filter(site => site.status === 'COMPLETED').length / mockSites.length) * 100)
//   };

//   // Dummy analytics data
//   const analyticsData = {
//     monthlyProgress: [
//       { month: 'Jan', completed: 2, total: 5 },
//       { month: 'Feb', completed: 3, total: 8 },
//       { month: 'Mar', completed: 5, total: 12 },
//       { month: 'Apr', completed: 7, total: 15 },
//     ],
//     teamPerformance: [
//       { name: 'Site Completion', value: mockStatistics.completion_percentage, color: 'green' },
//       { name: 'Budget Utilization', value: 65, color: 'blue' },
//       { name: 'Timeline Adherence', value: 80, color: 'purple' },
//     ]
//   };

//   return (
//     <div className="space-y-4">
//       <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
//         <h3 className="text-base font-semibold text-gray-900 mb-4">Project Analytics</h3>

//         {/* Key Metrics */}
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
//           {analyticsData.teamPerformance.map((metric, index) => (
//             <div key={index} className="text-center">
//               <div className="relative w-16 h-16 mx-auto mb-2">
//                 <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
//                   <path
//                     d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
//                     fill="none"
//                     stroke="#e5e7eb"
//                     strokeWidth="2"
//                   />
//                   <path
//                     d="m18,2.0845 a 15.9155,15.9155 0 0,1 0,31.831 a 15.9155,15.9155 0 0,1 0,-31.831"
//                     fill="none"
//                     stroke={metric.color === 'green' ? '#10b981' : metric.color === 'blue' ? '#3b82f6' : '#8b5cf6'}
//                     strokeWidth="2"
//                     strokeDasharray={`${metric.value}, 100`}
//                   />
//                 </svg>
//                 <div className="absolute inset-0 flex items-center justify-center">
//                   <span className="text-base font-bold text-gray-900">{metric.value}%</span>
//                 </div>
//               </div>
//               <h4 className="text-sm font-medium text-gray-900">{metric.name}</h4>
//             </div>
//           ))}
//         </div>

//         {/* Progress Overview */}
//         <div className="border-t border-gray-200 pt-4">
//           <h4 className="text-sm font-medium text-gray-900 mb-3">Monthly Progress</h4>
//           <div className="space-y-2">
//             {analyticsData.monthlyProgress.map((month, index) => (
//               <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
//                 <span className="text-sm font-medium text-gray-900">{month.month}</span>
//                 <div className="flex items-center gap-3">
//                   <span className="text-xs text-gray-600">{month.completed}/{month.total} sites</span>
//                   <div className="w-20 h-1.5 bg-gray-200 rounded-full">
//                     <div
//                       className="h-1.5 bg-blue-600 rounded-full"
//                       style={{ width: `${(month.completed / month.total) * 100}%` }}
//                     ></div>
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// Main ProjectDetails Component
const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiInfo },
    { id: 'team', label: 'Team', icon: FiUsers },
    // { id: 'sites', label: 'Sites', icon: FiMapPin },
    // { id: 'analytics', label: 'Analytics', icon: FiTrendingUp },
  ];

  const fetchProjectData = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/projects/${id}/details`);

      // Replace the sites data with mock sites
      const projectDataWithMockSites = {
        ...response.data.data,
        project: {
          ...response.data.data.project,
        },
       
      };

      setProjectData(projectDataWithMockSites);
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProjectData();
    }
  }, [id]);

  const renderTabContent = () => {
    if (!projectData) return null;

    switch (activeTab) {
      case 'overview':
        return <ProjectOverviewTab data={projectData} />;
      case 'team':
        return <TeamMembersTab data={projectData} />;
      case 'logs':
        return <EntityLogs entity="project" entityId={id as string} />

      default:
        return <ProjectOverviewTab data={projectData} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Project Not Found</h2>
          <p className="text-gray-600">The project you're looking for doesn't exist or you don't have permission to view it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Project Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded flex items-center justify-center text-white text-sm font-bold">
              {projectData.project.project_name.substring(0, 2).toUpperCase()}
            </div>

            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">
                {projectData.project.project_name}
              </h1>
              <p className="text-sm text-gray-600 mb-1">{projectData.project.project_short_name}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <FiUser size={12} />
                  {projectData.project.client.client_full_name}
                </div>
                {projectData.project.budget && <div className="flex items-center gap-1">
                  <FaRupeeSign size={12} />
                  {parseFloat(projectData.project.budget).toLocaleString()}
                </div>}
                <div className="flex items-center gap-1">
                  <FiCalendar size={12} />
                  Created {new Date(projectData.project.created_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${projectData.project.status === 'ACTIVE' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  {projectData.project.status}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded shadow-sm border border-gray-200  mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ProjectDetails;